// --- Core ---
import {ReactElement} from "react";
import {FormattedMessage} from "react-intl";
// --- Material UI ---
import {Button, ButtonProps, CircularProgress, Grid, IconButton, IconButtonProps, Stack, Tooltip, TooltipProps, Typography} from "@mui/material";
// Icons
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import DeleteIcon from "@mui/icons-material/Delete";
// --- SaniQ ---
import {usePermission} from "helpers/Permissions";
import {PermissionAccessLevel, PermissionKey} from "vendor/qurasoft/definitions/types/permissions";

type SaniQButtonProps = Omit<ButtonProps, "title"> & {
  access?: PermissionAccessLevel;
  // Shows a loading circle left of the button text and disables the button. Prefer loading
  isLoading?: boolean;
  permission?: PermissionKey;
  title?: ReactElement;
  loadingWithTitle?: boolean;
};

/**
 * Default SaniQ back button.
 * Is shown with an icon.
 */
export function BackButton(props: SaniQButtonProps) {
  return (
    <LoadingButton
      title={<FormattedMessage id="dialog.back" defaultMessage="Back"/>}
      startIcon={<ArrowBackIosIcon/>}
      {...props}
    />
  );
}

/**
 * Default SaniQ cancel button.
 */
export function CancelButton(props: SaniQButtonProps) {
  return (
    <LoadingButton
      title={<FormattedMessage id="dialog.cancel" defaultMessage="Cancel"/>}
      {...props}
    />
  );
}

/**
 * Default SaniQ close button.
 */
export function CloseButton(props: SaniQButtonProps) {
  return (
    <LoadingButton
      title={<FormattedMessage id="dialog.close" defaultMessage="Close"/>}
      {...props}
    />
  );
}
const deleteLabel = <FormattedMessage id="dialog.delete" defaultMessage="Delete"/>;

/**
 * Default SaniQ delete button.
 */
export function DeleteButton(props: SaniQButtonProps) {
  return (
    <LoadingButton
      title={deleteLabel}
      variant="contained"
      {...props}
    />
  );
}
type DeleteIconButtonProps = IconButtonProps & {
  tooltipProps?: TooltipProps;
};

/**
 * Default SaniQ delete icon button for usage in lists and action bars.
 */
export const DeleteIconButton = ({tooltipProps, ...otherProps}: DeleteIconButtonProps) => (
  <Tooltip title={deleteLabel} {...tooltipProps}>
    <IconButton {...otherProps}>
      <DeleteIcon color="error"/>
    </IconButton>
  </Tooltip>
);

/**
 * Default SaniQ save button.
 */
export function SaveButton(props: SaniQButtonProps) {
  return (
    <LoadingButton
      title={props.title ?? <FormattedMessage id="dialog.save" defaultMessage="Save"/>}
      variant="contained"
      access={"write"}
      {...props}
    />
  );
}

/**
 * Default SaniQ button base.
 *
 * Allows custom button text, has a loading and disabled state and can show a custom Icon.
 *
 * All other props are forwarded to the MUI LoadingButton.
 */
const LoadingButton = (
  {
    access = "read",
    children,
    isLoading,
    permission,
    title = <FormattedMessage id="dialog.save" defaultMessage="Save"/>,
    startIcon,
    loadingWithTitle = false,
    ...rest
  }: SaniQButtonProps
) => {
  const hasPermission = usePermission(permission, access, true);

  if (!hasPermission) {
    return null;
  }

  return (
    <Button
      loading={isLoading}
      startIcon={startIcon}
      loadingIndicator={
        loadingWithTitle ? (
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body1" sx={{ml: 2}}>
              {title}
            </Typography>
          </Stack>
        ) : undefined
      }
      {...rest}
    >
      <Grid
        container
        spacing={1}
        sx={{justifyContent: "center", alignItems: "center"}}
      >
        {
          children &&
          <Grid item>
            {children}
          </Grid>
        }
        <Grid item>
          {title}
        </Grid>
      </Grid>
    </Button>
  );
};

export {
  LoadingButton,
  SaniQButtonProps
};
